import { useCallback, useState } from 'react';
import {
  EditableListDataGridLayout,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EDITABLE_LIST_NESTED_LIST,
} from 'framework/list';
import { Grid2 as Grid, Stack, Typography } from '@mui/material';
import { AnchorField, TextField } from 'framework/ui/field';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import BillingTypeField from './fields/BillingTypeField';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import TotalHeader from './fields/totalFields/TotalHeader';
import TotalField from './fields/totalFields/TotalField';
import FTEField from './fields/fteFields/FTEField';
import ProjectSowNameField from './fields/sowFields/ProjectSowNameField';
import { getWithThousandsSeparator } from 'framework/utils';
import BudgetSourceField from './fields/BudgetSourceField';
import BudgetAmountField from './fields/BudgetAmountField';
import BudgetDateField from './fields/BudgetDateField';
import BudgetHoursField from './fields/BudgetHoursField';
import { useNavigate } from 'react-router-dom';
import { useAuthorization } from 'framework/authorization';
import { EditFormShowButton } from 'framework/form';
import {
  useProjectDataContext,
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
  BUDGET_UPDATE,
  useNewProjectUrl,
  ProjectSectionAccordion,
} from 'domain/shared/newProject';

const MilestonesBasedBudget = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { getUpdateBudgetUrl } = useNewProjectUrl();
  const navigate = useNavigate();
  const { isGranted } = useAuthorization();
  const { data } = useProjectDataContext();
  const { isAccessGranted } = useProjectSecurityContext();

  const toggleVisible = (newStateVisible: boolean) => {
    setIsVisible(newStateVisible);
  };

  const renderActions = useCallback(() => {
    if (!isGranted(BUDGET_UPDATE) && !isAccessGranted(ACCESS_INFO_SECTIONS.BUDGET, ACCESS_INFO_ACTIONS.MANAGE)) {
      return [];
    }
    return [
      <IconButton sx={{ p: 0, ml: 1 }} key="add" onClick={() => navigate(getUpdateBudgetUrl(data.id!))}>
        <EditFormShowButton />
      </IconButton>,
    ];
  }, [getUpdateBudgetUrl, data, isAccessGranted, isGranted, navigate]);

  return (
    <ProjectSectionAccordion
      defaultExpanded
      title={
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="h4" color="primary" pl={2}>
              Budget
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            {isVisible && (
              <IconButton onClick={() => toggleVisible(false)} size="small">
                <VisibilityIcon sx={{ color: 'primary.main' }} />
              </IconButton>
            )}
            {!isVisible && (
              <IconButton onClick={() => toggleVisible(true)} size="small">
                <VisibilityOff sx={{ color: 'primary.main' }} />
              </IconButton>
            )}
            <>{renderActions()}</>
          </Stack>
        </Stack>
      }
    >
      <EditableListDataGridLayout
        rerenderTableCellsDeps={[isVisible]}
        getTableCells={isEditModeLayout =>
          Promise.resolve([
            {
              type: EDITABLE_LIST_GROUP,
              component: Stack,
              componentProps: {
                spacing: 1,
              },
              gridCellProps: {
                size: 12,
                borderBottom: '1px solid var(--mui-palette-border-default)',
              },
              children: [
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Stack,
                  componentProps: {
                    spacing: 1,
                    direction: 'row',
                  },
                  gridCellProps: {
                    size: 12,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'id',
                      isEditor: false,
                      component: AnchorField,
                      gridCellProps: { size: 0.5 },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'billingType',
                      isEditor: false,
                      component: BillingTypeField,
                      componentProps: { isPrestart: 'state' },
                      gridCellProps: { size: 3, alignItems: 'start' },
                    },
                    {
                      type: EDITABLE_LIST_NESTED_LIST,
                      source: 'structure',
                      gridCellProps: { size: 8.5 },
                      component: Grid,
                      componentProps: { direction: 'row', container: true },
                      children: [
                        {
                          type: EDITABLE_LIST_CUSTOM,

                          isEditor: false,
                          component: BudgetAmountField,
                          componentProps: {
                            source: 'finance',
                            status: 'finance.status',
                            showContent: isVisible,
                          },
                          gridCellProps: { size: 2.5, alignItems: 'start' },
                        },
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: BudgetHoursField,
                          componentProps: {
                            source: 'budget.hours',
                            status: 'finance.status',
                          },
                          gridCellProps: { size: 2, alignItems: 'start' },
                        },
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: BudgetDateField,
                          componentProps: {
                            source: 'source',
                            date: 'budget.effectiveStartDate',
                            fallbackDate: 'finance.createdAt',
                            sowDate: 'budget.effectiveStartDate',
                            tempDate: 'budgetEndDate',
                          },
                          gridCellProps: { size: 2.5, alignItems: 'start' },
                        },
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: BudgetDateField,
                          componentProps: {
                            source: 'source',
                            date: 'budget.effectiveEndDate',
                            sowDate: 'budget.effectiveEndDate',
                            tempDate: 'budgetEndDate',
                          },
                          gridCellProps: { size: 2.5, alignItems: 'start' },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'budget',
                          isEditor: false,
                          component: BudgetSourceField,
                          componentProps: {
                            financeBudgetSource: 'finance',
                          },
                          gridCellProps: { size: 2.5, alignItems: 'start', justifyContent: 'end', display: 'flex' },
                        },
                      ],
                    },
                  ],
                },
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Stack,
                  componentProps: {
                    spacing: 1,
                    direction: 'row',
                  },
                  gridCellProps: {
                    size: 12,
                    fontWeight: 800,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: Typography,
                      gridCellProps: {
                        size: 0.5,
                      },
                    },
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: TotalHeader,
                      componentProps: {
                        justifyContent: 'end',
                      },
                      gridCellProps: {
                        size: 3,
                      },
                    },
                    {
                      type: EDITABLE_LIST_GROUP,
                      component: Grid,
                      componentProps: {
                        container: true,
                        direction: 'row',
                      },
                      gridCellProps: { size: 8.5 },
                      children: [
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'structure',
                          component: TotalField,
                          isEditor: false,
                          componentProps: {
                            field: 'amount',
                            showContent: isVisible,
                            calculateResult: (budgetData: any[], financeData: any[], showContent: boolean) => {
                              if (!showContent) return '*****';
                              return `${getWithThousandsSeparator(financeData.reduce((sum, val) => sum + (Number(val) || 0), 0))} USD`;
                            },
                          },
                          gridCellProps: {
                            size: 2.5,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          isEditor: false,
                          source: 'structure',
                          component: TotalField,
                          componentProps: {
                            field: 'hours',
                            showContent: isVisible,
                            calculateResult: (budgetData: object[]) => {
                              return `${budgetData.reduce((sum, val) => sum + (Number(val) || 0), 0).toFixed(2)} h`;
                            },
                          },
                          gridCellProps: {
                            size: 2,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          isEditor: false,
                          source: 'startDate',
                          component: TextField,
                          componentProps: {
                            className: { total: { fontWeight: 800 } },
                          },
                          gridCellProps: {
                            size: 2.5,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          isEditor: false,
                          source: 'endDate',
                          component: TextField,
                          componentProps: {
                            className: { total: { fontWeight: 800 } },
                          },
                          gridCellProps: {
                            size: 2.5,
                          },
                        },
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: Typography,
                          gridCellProps: {
                            size: 2.5,
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Stack,
                  componentProps: {
                    spacing: 1,
                    direction: 'row',
                  },
                  gridCellProps: {
                    size: 12,
                    borderTop: '1px solid var(--mui-palette-border-default)',
                    py: 1,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: Typography,
                      gridCellProps: { size: 0.5 },
                    },
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: ProjectSowNameField,
                      componentProps: {
                        sowIdSource: 'sowId',
                        projectNameSource: 'projectName',
                      },
                      gridCellProps: { size: 3 },
                    },
                    {
                      type: EDITABLE_LIST_GROUP,
                      component: Grid,
                      componentProps: {
                        spacing: 1,
                        direction: 'row',
                        container: true,
                      },
                      gridCellProps: { size: 8.5 },
                      children: [
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: FTEField,
                          componentProps: {
                            fteSource: 'fte',
                          },
                          gridCellProps: {
                            size: 12,
                            sx: { '.MuiFormControl-root': { mb: 0 } },
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ])
        }
      />
    </ProjectSectionAccordion>
  );
};

export default MilestonesBasedBudget;
